import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import type {
  IComment,
  ICommentDeleteRequest,
  ICommentReaction,
  ICommentReactionRequest,
  ICommentReport,
  ICommentReportRequest,
  ICommentRequest,
  ICommentRestoreRequest,
  ICommentsResponse,
  ICommentUpdate,
  ICommentUpdateRequest,
  INewComment,
} from '@rpgc-ai/shared-interfaces/interfaces/comment.interface';

import type { Observable } from 'rxjs';

import { SKIP_GLOBAL_ERROR_NOTIFICATION } from '../auth/auth.interceptor';

@Injectable({
  providedIn: 'root',
})
export class ChatbotCommentsService {
  readonly #http = inject(HttpClient);

  getComments(body: ICommentRequest): Observable<ICommentsResponse> {
    const { entityId, entityType, page, limit } = body;
    return this.#http.get<ICommentsResponse>('/comments', {
      params: {
        entityType,
        entityId,
        page,
        limit,
      },
      context: new HttpContext().set(SKIP_GLOBAL_ERROR_NOTIFICATION, true),
    });
  }

  createNewComment(body: INewComment): Observable<IComment> {
    return this.#http.post<IComment>('/comments', body);
  }

  updateComment(body: ICommentUpdateRequest): Observable<IComment> {
    const { id, content } = body;
    const requestBody: ICommentUpdate = { content };
    return this.#http.patch<IComment>(`/comments/${id}`, requestBody);
  }

  deleteComment(body: ICommentDeleteRequest): Observable<void> {
    const { id } = body;
    return this.#http.delete<void>(`/comments/${id}`);
  }

  restoreComment(body: ICommentRestoreRequest): Observable<IComment> {
    const { id } = body;
    return this.#http.patch<IComment>(`/comments/${id}/restore`, {});
  }

  updateCommentReaction(body: ICommentReactionRequest): Observable<void> {
    const { id, reaction } = body;
    const requestBody: ICommentReaction = { reaction };
    return this.#http.put<void>(`/comments/${id}/reactions`, requestBody);
  }

  reportComment(body: ICommentReportRequest): Observable<void> {
    const { id, reason, details } = body;
    const requestBody: ICommentReport = { reason, details };
    return this.#http.post<void>(`/comments/${id}/reports`, requestBody, {
      context: new HttpContext().set(SKIP_GLOBAL_ERROR_NOTIFICATION, true),
    });
  }
}
